import {computed, observable, makeObservable, action, autorun} from "mobx";
import type {AppState} from "../state/AppState";
import {getDefaultInsertionInfo} from "../../ui/modules/catalog/create/CatalogTypes";
import {XyiconFeature} from "../../generated/api/base";
import type {
	PortTemplateDto,
	CatalogIconType,
	XyiconCatalogDto,
	XyiconCatalogIconDto,
	Color,
	XyiconCatalogsUpdateFieldDto,
} from "../../generated/api/base";
import {ImageUploadPreprocessor} from "../../utils/image/ImageUploadPreprocessor";
import {ArrayUtils} from "../../utils/data/array/ArrayUtils";
import {XHRLoader} from "../../utils/loader/XHRLoader";
import {ImageUtils} from "../../utils/image/ImageUtils";
import type {OrgSearchResponseDto} from "../../generated/api/search";
import type {IModel} from "./Model";

export interface ICatalogSettings {
	xyiconFieldSettings: {
		visibleFields: string[];
	};
	catalogFieldSettings: {
		visibleFields: string[];
	};
	glyphChildrenColors: Color[];
	source: string;
}

export class Catalog implements IModel, XyiconCatalogDto {
	public static search(catalog: Catalog, search = "") {
		if (!search) {
			return true;
		}

		search = search.toLowerCase();
		const model = catalog.model.toLowerCase();

		if (model.includes(search)) {
			return true;
		}

		return false;
	}

	private readonly _appState: AppState;
	public readonly ownFeature = XyiconFeature.XyiconCatalog;

	private _data: XyiconCatalogDto;

	constructor(data: XyiconCatalogDto, appState: AppState) {
		makeObservable<this, "_data" | "updateSVGIconIfNeeded">(this, {
			_data: observable,
			isFavorite: computed,
			id: computed,
			refId: computed,
			fieldData: computed,
			typeId: computed,
			type: computed,
			typeName: computed,
			iconCategory: computed,
			iconData: computed,
			portTemplate: computed,
			glyphChildrenColors: computed,
			insertionInfo: computed,
			model: computed,
			icon: computed,
			updateSVGIconIfNeeded: action,
			thumbnail: computed,
			lastModifiedBy: computed,
			lastModifiedAt: computed,
			data: computed,
			settings: computed,
			xyiconVisibleFields: computed,
			catalogVisibleFields: computed,
			source: computed,
		});
		this._appState = appState;
		this.applyData(data);
	}

	public applyData(data: Partial<XyiconCatalogDto>) {
		this._data = data;
		/**
		 * Single quotes can cause problems in an icon
		 * when we set it as a background image with css.
		 * we can fix them with this workaround
		 */
		this.updateIconData(this._data.iconData, this._data.iconType, this._data.icon);
		this._data.fieldData = this._data.fieldData || {};
		this._data.portTemplate = this._data.portTemplate || [];

		this.applySettingsData();

		autorun(() => {
			this.updateSVGIconIfNeeded();
		});
	}

	public applySettingsData() {
		if (this._data.settings && this._data.settings !== "null") {
			if (typeof this._data.settings === "string") {
				this._data.settings = JSON.parse(this._data.settings);
			}

			if (!this._data.settings.catalogFieldSettings) {
				this._data.settings.catalogFieldSettings = {visibleFields: []};
			}
			if (!this._data.settings.xyiconFieldSettings) {
				this._data.settings.xyiconFieldSettings = {visibleFields: []};
			}

			if (!this._data.settings.glyphChildrenColors) {
				this._data.settings.glyphChildrenColors = [];
			}
		} else {
			const settings: ICatalogSettings = {
				xyiconFieldSettings: {
					visibleFields: [],
				},
				catalogFieldSettings: {
					visibleFields: [],
				},
				glyphChildrenColors: [],
				source: "",
			};

			this._data.settings = settings;
		}
	}

	public applyUpdate(data: XyiconCatalogsUpdateFieldDto) {
		if (data.xyiconCatalogIDList.includes(this.id)) {
			this.applyFieldValues(data.fieldValues);
			this._data.lastModifiedAt = data.lastModifiedAt || this._data.lastModifiedAt;
			this._data.lastModifiedBy = data.lastModifiedBy || this._data.lastModifiedBy;
		}
	}

	public applyFieldValues(fieldValues: Record<string, any> | null) {
		if (fieldValues?.model !== undefined) {
			this.setModel(fieldValues.model);
		}
	}

	public updateIconData(iconData: XyiconCatalogIconDto, iconCategory: CatalogIconType, icon: string) {
		this._data.iconData = iconData;
		this._data.iconType = iconCategory;
		/**
		 * Single quotes can cause problems in an icon
		 * when we set it as a background image with css.
		 * we can fix them with this workaround: replace them with "apostrophe"
		 */
		this._data.icon = icon.replace(/'/g, "\u2018").replace(/\n\s*/g, ""); // replace single quotes, and remove white-spaces;
	}

	public setFavorite(value: boolean) {
		const user = this._appState.user;
		const {favoriteCatalogs} = user;

		if (value) {
			ArrayUtils.addMutable(favoriteCatalogs, this.id);
		} else {
			ArrayUtils.removeMutable(favoriteCatalogs, this.id);
		}

		return this._appState.app.transport.requestForOrganization({
			url: "users/markentitiesasfavorites",
			method: XHRLoader.METHOD_POST,
			params: {
				entityIDList: [this.id],
				feature: this.ownFeature,
				isFavorite: value,
			},
		});
	}

	public get isFavorite(): boolean {
		const arr = this._appState.user?.favoriteCatalogs || [];

		return arr.includes(this.id);
	}

	public get id() {
		return this._data.xyiconCatalogID;
	}

	public get refId() {
		return this._data.xyiconCatalogRefID;
	}

	public setFieldData(fieldData: {[refId: string]: any}) {
		this._data.fieldData = {...fieldData};
	}

	public get fieldData() {
		return this._data.fieldData;
	}

	public get linkedFieldData() {
		return (this._data as OrgSearchResponseDto).linkedFieldData ?? {};
	}

	public set typeId(value: string) {
		this._data.xyiconTypeID = value;
	}

	public get typeId() {
		return this._data.xyiconTypeID ?? "";
	}

	public get type() {
		return this._appState.actions.getTypeById(this.typeId);
	}

	public get typeName() {
		return this.type?.name || "";
	}

	public get iconCategory() {
		return this._data.iconType;
	}

	public get iconData() {
		return this._data.iconData;
	}

	public setPortTemplate(portTemplate: PortTemplateDto[]) {
		this._data.portTemplate = portTemplate;
	}

	public get portTemplate(): PortTemplateDto[] {
		return this._data.portTemplate;
	}

	public setModel(value: string) {
		this._data.model = value;
	}

	public setGlyphChildrenColors(colors: Color[]) {
		this._data.settings.glyphChildrenColors = colors;
	}

	public get glyphChildrenColors() {
		return this.settings.glyphChildrenColors;
	}

	public get insertionInfo() {
		return this._data.iconData.modelParameters?.insertionInfo ?? getDefaultInsertionInfo();
	}

	public get model() {
		return this._data.model;
	}

	public get icon() {
		return this._data.icon;
	}

	private updateSVGIconIfNeeded() {
		const dataIcon = ImageUtils.embedFontIntoSvgIfNecessary(this._data.icon, this._appState.fonts);
		if (this._data.icon !== dataIcon) {
			this._data.icon = dataIcon;
		}
	}

	public get thumbnail() {
		return ImageUploadPreprocessor.changeProblematicCharactersToURLEncode(this._data.icon);
	}

	public set lastModifiedBy(value: string) {
		this._data.lastModifiedBy = value;
	}

	public get lastModifiedBy() {
		return this._data.lastModifiedBy;
	}

	public set lastModifiedAt(value: string) {
		this._data.lastModifiedAt = value;
	}

	public get lastModifiedAt() {
		return this._data.lastModifiedAt;
	}

	public set model(value: string) {
		this._data.model = value;
	}

	public get data() {
		return this._data;
	}

	public get settings(): ICatalogSettings {
		return this._data.settings;
	}

	public get xyiconVisibleFields(): string[] {
		return this.settings.xyiconFieldSettings?.visibleFields || [];
	}

	public get catalogVisibleFields(): string[] {
		return this.settings.catalogFieldSettings?.visibleFields || [];
	}

	public get source() {
		return this.settings.source;
	}
}
